import { useEffect, useMemo, useState } from 'react';
import { projects } from './generated/projects';
import type { Project } from './data/projectMeta';
import './index.css';

const ALL = 'All';

function categoryCounts(list: Project[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const project of list) {
    counts.set(project.category, (counts.get(project.category) ?? 0) + 1);
  }
  return counts;
}

function allTags(list: Project[]): string[] {
  const set = new Set<string>();
  for (const project of list) {
    for (const tag of project.tags) set.add(tag);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

function matchesQuery(project: Project, query: string): boolean {
  if (query.length === 0) return true;
  const q = query.toLowerCase();
  const haystack = [project.title, project.description, project.slug, project.category, ...project.tags]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

function projectUrl(slug: string): string {
  return `/${slug}/`;
}

const inputClass =
  'w-full rounded-lg border border-slate-700 bg-slate-900/70 px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';

const chipBase =
  'rounded-full border px-3 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500';

function categoryChipClass(active: boolean): string {
  return `${chipBase} ${
    active
      ? 'border-indigo-400 bg-indigo-500 text-white'
      : 'border-slate-700 bg-slate-900/60 text-slate-300 hover:border-slate-500 hover:text-white'
  }`;
}

function tagChipClass(active: boolean): string {
  return `${chipBase} ${
    active
      ? 'border-emerald-400 bg-emerald-500 text-white'
      : 'border-slate-700 bg-slate-900/40 text-slate-400 hover:border-slate-500 hover:text-slate-200'
  }`;
}

const cardClass =
  'group flex flex-col rounded-xl border border-slate-800 bg-slate-900/50 p-4 transition-all hover:border-indigo-500/60 hover:bg-slate-900 focus-within:border-indigo-500';

export default function App() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string>(ALL);
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set());

  // Keep the URL hash in sync with the query so it survives reloads and is shareable.
  useEffect(() => {
    const initial = window.location.hash.replace(/^#/, '');
    if (initial.length > 0) setQuery(initial);
    const onHash = () => setQuery(window.location.hash.replace(/^#/, ''));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    const next = query.length > 0 ? `#${query}` : '';
    if (next !== window.location.hash) {
      window.history.replaceState(null, '', next || window.location.pathname);
    }
  }, [query]);

  const toggleTag = (tag: string) => {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  const clearFilters = () => {
    setQuery('');
    setCategory(ALL);
    setActiveTags(new Set());
  };

  const categories = useMemo(() => {
    const counts = categoryCounts(projects);
    return [ALL, ...[...counts.keys()].sort((a, b) => a.localeCompare(b))];
  }, []);

  const tags = useMemo(() => allTags(projects), []);

  const filtered = useMemo(() => {
    const q = query.trim();
    return projects
      .filter((project) => matchesQuery(project, q))
      .filter((project) => category === ALL || project.category === category)
      .filter(
        (project) =>
          activeTags.size === 0 || [...activeTags].every((tag) => project.tags.includes(tag)),
      )
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [query, category, activeTags]);

  const hasFilters = query.length > 0 || category !== ALL || activeTags.size > 0;

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400">palplanner</p>
        <h1 className="mt-1 text-3xl font-bold text-white sm:text-4xl">labs</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">
          A searchable dashboard of {projects.length} self-contained browser experiments. Search,
          filter, and open any project.
        </p>
      </header>

      <div className="mb-6 flex flex-col gap-3">
        <input
          aria-label="Search lab projects"
          autoComplete="off"
          autoFocus
          className={inputClass}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by name, description, or tag…"
          type="search"
          value={query}
        />

        <div
          aria-label="Filter by category"
          className="flex flex-wrap gap-2"
          role="group"
        >
          {categories.map((label) => (
            <button
              className={categoryChipClass(category === label)}
              key={label}
              onClick={() => setCategory(label)}
              type="button"
            >
              {label}
              {label !== ALL && (
                <span className="ml-1.5 text-[10px] opacity-70">
                  {categoryCounts(projects).get(label) ?? 0}
                </span>
              )}
            </button>
          ))}
        </div>

        {tags.length > 0 && (
          <div aria-label="Filter by tag" className="flex flex-wrap gap-1.5" role="group">
            {tags.map((tag) => (
              <button
                aria-pressed={activeTags.has(tag)}
                className={tagChipClass(activeTags.has(tag))}
                key={tag}
                onClick={() => toggleTag(tag)}
                type="button"
              >
                #{tag}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mb-4 flex items-center justify-between text-xs text-slate-500">
        <span>
          Showing <span className="font-semibold text-slate-300">{filtered.length}</span> of{' '}
          {projects.length}
        </span>
        {hasFilters && (
          <button
            className="rounded-md px-2 py-1 text-indigo-400 hover:text-indigo-300 hover:underline"
            onClick={clearFilters}
            type="button"
          >
            Clear filters
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <p className="rounded-xl border border-slate-800 bg-slate-900/40 p-8 text-center text-sm text-slate-400">
          No projects match your filters.
        </p>
      ) : (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((project) => (
            <li key={project.slug}>
              <a className={cardClass} href={projectUrl(project.slug)}>
                <div className="flex items-start justify-between gap-2">
                  <h2 className="text-sm font-semibold text-white group-hover:text-indigo-300">
                    {project.title}
                  </h2>
                  <span className="shrink-0 rounded-md bg-slate-800 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                    {project.category}
                  </span>
                </div>
                {project.description.length > 0 && (
                  <p className="mt-2 line-clamp-2 text-xs text-slate-400">{project.description}</p>
                )}
                {project.tags.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {project.tags.map((tag) => (
                      <span
                        className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[10px] text-slate-500"
                        key={tag}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
                <span className="mt-auto pt-3 text-[11px] font-medium text-indigo-400 opacity-0 transition-opacity group-hover:opacity-100">
                  Open project →
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}

      <footer className="mt-12 border-t border-slate-800 pt-6 text-center text-xs text-slate-600">
        {projects.length} projects · auto-discovered from the lab folder
      </footer>
    </div>
  );
}
